import { Injectable, Optional, Inject } from '@angular/core';
import { ReplaySubject, Observable, throwError } from 'rxjs';
import { Buffer } from 'buffer';
import _ from 'lodash';
import * as loggly from 'loggly-jslogger';
import { environment } from '../environments/environment';
import { IStorageService } from './storage.interface';

const redactedFields: string[] = [
  'cacheToken',
  'accessToken',
  'idToken',
  'refreshToken',
  'intercomTokens',
];

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  private storage: IStorageService | null = null;

  // flag to turn on all debug messages
  public DEBUG_MESSAGES: boolean = true;
  
  // create global page change subject that app component can subscribe to
  // IMPORTANT: all programmatic page changes for side nav must call next on this to change page
  //    currently used from welcome page and user service
  public openPage: ReplaySubject<any>; 
  public openUrl: ReplaySubject<any>; 
  public openExtUrl: ReplaySubject<any>; 
  
  // create global error subject, that many can update and can be subscribed to for display, etc
  public errorSubject: ReplaySubject<any>; // make replay so as we subscribe we do not lose any old updates
  
  // other subjects to call ionic code from multiple places
  public openChatSubject: ReplaySubject<any>; // to open chat window from anywhere; executed in app.component
  public offlineSubject: ReplaySubject<boolean>;
  public offline: boolean = false; // must be online initially since web app does not sense network changes 
  public intercomState: ReplaySubject<any>;
  public intercomInitialized: boolean = false;
  public intercomDisable: boolean = false; // set to true for testing to see if problem is due to intercom
  public embedded: boolean = false; // flag set from app.component

  // deep link URL to navigate to after auth/data loads (set by app.component on startup)
  public pendingDeepLink: string | null = null;

  // flag indicating user view was loaded via deep link (no underlying view in nav stack)
  public isDeepLinkedUserView: boolean = false;

  // following set from config in constructor
  public environment: string; 
  public version: string; 
  public platform: string = 'client-ionic'; // will be updated by app.component based on actual platform detection
  public pwa: boolean = false;
  public homeUrl: string;
  public apiUrl: string;
  public cacheUrl: string;
  
  // initially set to 'unknown' so we know they haven't logged in yet
  public userEmail: string = 'unknown';

  constructor(@Optional() @Inject('STORAGE_SERVICE') private storageService?: IStorageService) {
    this.errorSubject = new ReplaySubject(2); // only store two to avoid buffering up a bunch while user is away (happened for PWA on computer)
    this.openChatSubject = new ReplaySubject();
    this.openPage = new ReplaySubject();
    this.openUrl = new ReplaySubject();
    this.openExtUrl = new ReplaySubject();
    this.intercomState = new ReplaySubject();
    this.offlineSubject = new ReplaySubject(2);
    
    // Initialize storage
    this.initStorage(); 

    // set flags based on signals from app.component
    this.offlineSubject.subscribe((isOffline: boolean) => {
      this.offline = isOffline;
    });
    this.intercomState.subscribe((state: string) => {
      if (state === 'initialized') {
        this.intercomInitialized = true;
      } else if (state === 'shutdown') {
        this.intercomInitialized = false;        
      }
    });

    // determine environment from Angular environment system
    this.environment = environment.env;
    this.version = environment.version;
    // platform will be set by app.component based on actual detection
    this.apiUrl = environment.apiUrl;
    this.homeUrl = environment.homeUrl;
    this.cacheUrl = environment.cacheUrl;
  
    // handle special dev-livecb hostname override if needed
    if (typeof window !== 'undefined' && window.location && window.location.hostname === 'dev-livecb.teamtoolsapp.com') { 
      this.environment = 'dev-livecb';
    }

    // set up loggly 
    let _LTracker = loggly._LTracker || [];
    _LTracker.push({
      'logglyKey': 'aaea219d-85b0-4a7d-98d2-42ee7c03d796',
      'sendConsoleErrors': true,
      'tag': 'ionic-client,' + this.environment
    });
  }

  // logging
  private logIt(type: string, msg: string, obj?: any) {
    // redact any private data, but don't modify passed in obj
    let logObj = obj ? _.cloneDeep(obj) : {};    
    if (obj) {
      for (let rField of redactedFields) {
        if (_.has(obj, rField) && obj[rField] !== '') {
          logObj[rField] = 'redacted';
        }
      }
    }  

    // if obj is string, put in obj since otherwise it messes up loggly
    if (_.isString(obj)) {
      logObj = { objectMessage: obj };
    }

    // only do loggly if staging or prod
    if (this.environment === 'prod' || this.environment === 'staging') {
      let logData: any = {
        'env': this.environment,
        'platform': this.platform,
        't2version': this.version,
        'user': this.userEmail,
        'level': type,
        'message': msg,
      };
      if (obj) {
        // stringify data to avoid maxing out loggly indexed fields
        logData['data'] = JSON.stringify(logObj);
      } 
      loggly._LTracker.push(logData);
    }

    // always do console unless production
    if (this.environment === 'prod') {
      // do nothing
    } else if (obj) {
      // this causes errors in ios simulator
      // see https://github.com/ionic-team/ionic-app-scripts/issues/970
      type === 'error' ? console.warn(msg, logObj) : console.log(msg, logObj);
    } else {
      type === 'error' ? console.warn(msg) : console.log(msg);
    }
  }

  // public logging methods
  public logDebug(msg: string, obj?: any) {
    if (this.DEBUG_MESSAGES && this.environment !== 'prod') {
      this.logIt('debug', msg, obj);      
    }
  }

  public logInfo(msg: string, obj?: any) {
    this.logIt('info', msg, obj);
  }

  public logError(msg: string, obj?: any) {
    this.logIt('error', msg, obj);
  }

  // for errors, alert user, send to log, and maybe external error service
  public handleError(error: any): Observable<never> {
    let msg = 'unknown error';
    if (_.isString(error)) {
      msg = error;
    } else if (_.isObjectLike(error)) {
      let tmp = this.objDeepSearch('message', error);
     if (tmp) {
        msg = tmp;
      }
    }

    // prettify certain errors
    if (_.includes(msg, 'user canceled the sign-in flow')) {
      msg = 'Sign In Canceled!';
    }

    // update with error for user alert
    this.errorSubject.next(msg);

    // log it 
    this.logError(msg, error);

    // return observable that will be updated with error
    return throwError(() => error || 'handleError called with no arg');
  }

  // utility function to find the value of key in nested objects
  public objDeepSearch(key: string, obj: any): any {
    if (_.has(obj, key)) {
      return obj[key];
    } else {
      _.forEach(obj, (val: any, _subkey: any, searchObj: any) => {
        let tryJson = this.getJsonFromString(val);
        if (_.isString(val)) {
          if (tryJson) {
            val = tryJson;
          } else {
            let tryEscapedJson = this.getJsonFromString(val.replace(/\\"/g, '"'));
            if (tryEscapedJson) {
              val = tryEscapedJson;
            }
          }
        }  
        if (_.isObjectLike(val)) {
          let result = this.objDeepSearch(key, val); // recursive call
          if (result !== false) {
            (searchObj as any).objDeepSearchResult = result; // store in tmp field since passing in "this" is hard
            return false; // stops iteration
          } else {
            return true; // keep iterating; explicit return required by typescript compiler
          }
        } else {
          return true; // keep iterating; explicit return required by typescript compiler
        }
      });
    }
    if (_.has(obj, 'objDeepSearchResult')) {
      let ret = (obj as any).objDeepSearchResult;
      delete (obj as any).objDeepSearchResult; // remove tmp field to clean up
      return ret;
    } else {
      return false;
    }
  }

  // call to get json and handle bad data
  public getJsonFromString(str: string): any {
    try {
      let parsed = JSON.parse(str);
      return parsed;
    } catch (e) {
      return false;
    }
  }

  // copy properties to object
  public populatePropertiesFromObj(target: any, obj: any): void {
    Object.keys(obj).forEach((key) => {
      if (!_.isFunction(obj[key]) && !_.isUndefined(obj[key])) {
        target[key] = obj[key];
      }
    });
  }
  
  // e.g. https://app.teamtoolsapp.com/?view=orgchart&embed=true returns: 
  //   getUrlInputs(view) returns { path: '', param: 'orgchart' }
  //   getUrlInputs(embed) returns { path: '', param: 'true' }
  // order is not relevant
  // also e.g. https://app.teamtoolsapp.com/view/orgchart/embed returns: 
  //   getUrlInputs(view) returns { path: 'orgchart', param: '' }
  //   getUrlInputs(embed) returns { path: 'embed', param: '' }
  // embed must be the last segment
  public getUrlInputs(input: string): { path: string; param: string } {
    let ret = {
      path: '',
      param: '',
    };
    
    if (typeof window === 'undefined' || !window.location) {
      return ret;
    }
    
    if (location && location.href && location.pathname) {
      // First, check for query parameters
      let inputParam: string = '';
      if (location.search.split(input + '=').length > 1) {
        inputParam = location.search.split(input + '=')[1];
        inputParam = inputParam.split('&')[0];
      }  
      
      // Then, check for path-based inputs
      let inputPath: string = '';
      let pathname = _.trim(location.pathname, '/');
      let pathParts = pathname.split('/');
      
      // Find the input string in the path parts
      let inputIndex = pathParts.indexOf(input);
      if (inputIndex !== -1) {
        // If we found the input string in the path, check what comes after it
        if (inputIndex + 1 < pathParts.length) {
          inputPath = pathParts[inputIndex + 1];
        } else {
          // If input is the last segment, return the input itself as path
          inputPath = input;
        }
      }
      
      // Set return values
      if (inputPath && inputPath !== '') {
        ret.path = inputPath;
      }
      if (inputParam && inputParam !== '') {
        ret.param = inputParam;
      }
    }
    return ret;
  }

  // to check if scopes granted includes full scopes
  public includesWords(target: string, words: string): boolean {
    return _.difference(_.words(words), _.words(target)).length === 0;
  }

  // Initialize Ionic Storage
  private async initStorage() {
    if (this.storageService) {
      this.storage = await this.storageService.create();
    }
  }
  
  // storage methods using Ionic Storage for cross-platform compatibility
  public async getFromStorage(fieldName: string, defaultVal: string): Promise<string> {
    try {
      // Ensure storage is initialized
      if (!this.storage) {
        await this.initStorage();
      }
      
      const val = await this.storage!.get(fieldName);
      if (_.isString(val) && val !== '' && val !== null) {
        return val;        
      } else {
        return defaultVal;        
      }
    } catch (reason: any) {
      this.logInfo('error getting ' + fieldName + ' from storage', reason);
      return defaultVal;
    }
  }

  public async writeToStorage(fieldName: string, val: string): Promise<string> {
    try {
      // Ensure storage is initialized
      if (!this.storage) {
        await this.initStorage();
      }
      
      await this.storage!.set(fieldName, val);
      return 'ok';
    } catch (reason: any) {
      this.logError('error writing ' + fieldName + ' to storage', reason);
      return 'err';
    }
  }

  // encoding utilities using Buffer polyfill for perfect compatibility
  public encodeObj(obj: any): string {
    const jsonString = JSON.stringify(obj);
    // Use the Buffer polyfill which works identically to Node.js Buffer
    return Buffer.from(jsonString).toString('base64');
  }

  public decodeObj(str: string): any {
    if (!str) {
      return null;
    }

    try {
      // Use the Buffer polyfill for decoding
      const jsonString = Buffer.from(str, 'base64').toString();
      return JSON.parse(jsonString);
    } catch (e) {
      // If base64 decode fails, try plain JSON as fallback
      try {
        return JSON.parse(str);
      } catch (e2) {
        this.logError('Error decoding object', e);
        return null;
      }
    }
  }
}